/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useEffect } from 'react';
import API_CONFIG from '../lib/config';
import { Vendor } from '../types';

interface VendorsResponse {
  success: boolean;
  nextCursor: string | null;
  data: Vendor[];
}

interface UseVendorsReturn {
  vendors: Vendor[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  reset: () => Promise<void>;
}

interface UseVendorsOptions {
  isSubscribe?: boolean;
}

const LIMIT = 4;

type VendorsCacheKey = 'gift' | 'subscription';

const vendorCache: Record<VendorsCacheKey, Vendor[] | null> = {
  gift: null,
  subscription: null,
};

const vendorCursorCache: Record<VendorsCacheKey, string | null> = {
  gift: null,
  subscription: null,
};

const vendorInflightCache: Record<VendorsCacheKey, Promise<void> | null> = {
  gift: null,
  subscription: null,
};

const getApiBaseUrl = (scope: VendorsCacheKey): string =>
  scope === 'subscription' ? API_CONFIG.SUBSCRIPTION_BASE_URL : API_CONFIG.BASE_URL;

const transformVendor = (vendor: Vendor, scope: VendorsCacheKey): Vendor => ({
  ...vendor,
  id: vendor._id,
  emoji: getEmojiForVendor(vendor.type || 'general'),
  rating: scope === 'subscription' ? undefined : vendor.rating || 4.5,
  deliveryTime:
    scope === 'subscription' ? undefined : formatDeliveryTime(vendor.deliveryTime),
});

const fetchVendorPage = async (
  scope: VendorsCacheKey,
  cursor?: string | null
): Promise<{ vendors: Vendor[]; nextCursor: string | null }> => {
  let url = `${getApiBaseUrl(scope)}/vendors?limit=${LIMIT}`;
  if (cursor) {
    url += `&cursor=${encodeURIComponent(cursor)}`;
  }

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch vendors: ${response.statusText}`);
  }

  const data: VendorsResponse = await response.json();
  if (!data.success) {
    throw new Error('API returned unsuccessful response');
  }

  const transformedVendors = (data.data ?? []).map((vendor) => transformVendor(vendor, scope));

  return {
    vendors: transformedVendors,
    nextCursor: data.nextCursor,
  };
};

export const clearVendorsCache = (scope?: VendorsCacheKey): void => {
  if (scope) {
    vendorCache[scope] = null;
    vendorCursorCache[scope] = null;
    vendorInflightCache[scope] = null;
    return;
  }

  vendorCache.gift = null;
  vendorCache.subscription = null;
  vendorCursorCache.gift = null;
  vendorCursorCache.subscription = null;
  vendorInflightCache.gift = null;
  vendorInflightCache.subscription = null;
};

export const prefetchVendors = async (scope: VendorsCacheKey): Promise<void> => {
  if (vendorCache[scope] !== null) {
    return;
  }

  if (vendorInflightCache[scope]) {
    await vendorInflightCache[scope];
    return;
  }

  vendorInflightCache[scope] = fetchVendorPage(scope)
    .then(({ vendors, nextCursor }) => {
      vendorCache[scope] = vendors;
      vendorCursorCache[scope] = nextCursor;
    })
    .finally(() => {
      vendorInflightCache[scope] = null;
    });

  await vendorInflightCache[scope];
};

export const useVendors = ({ isSubscribe = false }: UseVendorsOptions = {}): UseVendorsReturn => {
  const cacheKey: VendorsCacheKey = isSubscribe ? 'subscription' : 'gift';
  const [vendors, setVendors] = useState<Vendor[]>(vendorCache[cacheKey] ?? []);
  const [nextCursor, setNextCursor] = useState<string | null>(vendorCursorCache[cacheKey]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(vendorCache[cacheKey] !== null);

  const fetchVendors = useCallback(
    async (cursor?: string | null, forceRefresh = false) => {
      try {
        setLoading(true);
        setError(null);

        if (!cursor && !forceRefresh) {
          await prefetchVendors(cacheKey);
          setVendors(vendorCache[cacheKey] ?? []);
          setNextCursor(vendorCursorCache[cacheKey]);
          setHasLoaded(true);
          return;
        }

        const { vendors: fetchedVendors, nextCursor: fetchedNextCursor } = await fetchVendorPage(
          cacheKey,
          cursor
        );

        const nextVendors = cursor
          ? [...(vendorCache[cacheKey] ?? vendors), ...fetchedVendors]
          : fetchedVendors;

        vendorCache[cacheKey] = nextVendors;
        vendorCursorCache[cacheKey] = fetchedNextCursor;

        setVendors(nextVendors);
        setNextCursor(fetchedNextCursor);
        setHasLoaded(true);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        console.error('Error fetching vendors:', err);
      } finally {
        setLoading(false);
      }
    },
    [cacheKey, vendors]
  );

  const loadMore = useCallback(async () => {
    if (nextCursor && !loading) {
      await fetchVendors(nextCursor);
    }
  }, [nextCursor, loading, fetchVendors]);

  const reset = useCallback(async () => {
    clearVendorsCache(cacheKey);
    setVendors([]);
    setNextCursor(null);
    setError(null);
    setHasLoaded(false);
    await fetchVendors(undefined, true);
  }, [cacheKey, fetchVendors]);

  useEffect(() => {
    if (!hasLoaded) {
      void fetchVendors();
    }
  }, [fetchVendors, hasLoaded]);

  return {
    vendors,
    loading,
    error,
    hasMore: nextCursor !== null,
    loadMore,
    reset,
  };
};

const getEmojiForVendor = (type: string): string => {
  const emojiMap: { [key: string]: string } = {
    'food-stuffs': '🍅',
    drugs: '💊',
    pharmacy: '💊',
    general: '🏪',
    'super-market': '📦',
    supermarket: '📦',
    fruits: '🌿',
    vegetables: '🌿',
    meat: '🍖',
    bakery: '🥖',
    address: '📍',
    retail: '🛒',
    convenience: '🏢',
  };
  return emojiMap[type.toLowerCase()] || '🏪';
};

const formatDeliveryTime = (deliveryTime: string | number | undefined): string => {
  if (!deliveryTime) {
    return 'TBD';
  }

  if (typeof deliveryTime === 'string') {
    return deliveryTime;
  }

  if (deliveryTime < 60) {
    return `${deliveryTime} mins`;
  }

  const hours = Math.floor(deliveryTime / 60);
  const mins = deliveryTime % 60;
  if (mins === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${mins}m`;
};
